import React, { useCallback, useEffect, useState } from "react";
import { Modal, ModalBody, Nav, NavItem, NavLink, TabContent, TabPane, Spinner, Button } from "reactstrap";
import api from "../../../services/api";
import { AUTOPILOT_COLORS } from "../../../components/autopilot/styles";
import IntegracaoTab from "../../Admin/Transportadoras/IntegracaoTab";
import { getCapacidadesTransportadora } from "../../../services/capacidadeTransportadoraApi";
import FreteTabelaResumoTab from "./FreteTabelaResumoTab";
import SftpTab from "./SftpTab";
import TransportadoraMonogram from "./TransportadoraMonogram";
import { CapacidadeBadge } from "./capacidadeBadges";
import { conviteEstado } from "./MinhasTransportadoras/conviteEstado";
import "../../../components/ChecklistRow/checklistRow.css";
import {
  drawerEyebrowStyle,
  drawerTitleStyle,
  drawerSubtitleStyle,
  drawerCloseButtonStyle,
  drawerFieldRowStyle,
  drawerFieldLabelStyle,
  drawerFieldValueStyle,
  drawerEmptyStyle,
} from "./detalheDrawer.styles";

// Modal de Detalhe da transportadora (PO1-3856): substitui o painel lateral
// (DetalheTransportadoraDrawer, H3 PO1-2620) no Catálogo e em Minhas
// Transportadoras; a lista volta a ocupar a largura toda. Mesmo conteúdo
// (cabeçalho, abas Visão Geral / Configuração / Tabela de Frete / SFTP) e
// mesmos endpoints: GET /transportadoras/:id/detalhe,
// GET /transportadoras/:id/capacidades (H4) e o IntegracaoTab existente.
// Casca no padrão de views/Admin/Transportadoras/TransportadoraDetalheModal.js.

const formatDoc = (v) => {
  const d = String(v || "").replace(/\D/g, "");
  if (d.length === 11) return d.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
  if (d.length === 14) return d.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, "$1.$2.$3/$4-$5");
  return v || "-";
};

const TABS = ["visao-geral", "configuracao", "tabela-frete", "sftp"];
const TAB_LABELS = {
  "visao-geral": "Visão Geral",
  configuracao: "Configuração",
  "tabela-frete": "Tabela de Frete",
  sftp: "SFTP",
};

const navLinkStyle = (ativo) => ({
  cursor: "pointer",
  fontSize: "0.8rem",
  padding: "0.55rem 0.8rem",
  color: ativo ? AUTOPILOT_COLORS.text : AUTOPILOT_COLORS.muted,
  borderBottom: `2px solid ${ativo ? AUTOPILOT_COLORS.accent : "transparent"}`,
  fontWeight: ativo ? 600 : 400,
});

const headStyle = {
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "space-between",
  gap: "0.75rem",
  padding: "1rem 1.5rem 0.75rem",
  borderBottom: `1px solid ${AUTOPILOT_COLORS.border}`,
};

const tabsStyle = {
  padding: "0 1.5rem",
  borderBottom: `1px solid ${AUTOPILOT_COLORS.border}`,
};

// O corpo cresce com o conteúdo e rola sozinho quando passa de 75% da janela.
const bodyStyle = {
  padding: "0.9rem 1.5rem 1.25rem",
  maxHeight: "75vh",
  overflowY: "auto",
};

// PO1-2299: props OPCIONAIS do acesso ao portal (o modal também é usado no
// Catálogo, que não as passa): `acessoPortal` = { temLoginAtivo, convite } da
// carteira e as ações onReenviar/onCancelar; `conviteHabilitado` esconde o
// Reenviar quando a jornada está desligada. PO1-3283: "Convidar" saiu — a
// entrada é "Adicionar transportadora" na carteira, e o convite nasce no
// onboarding; aqui só se reenvia/cancela um convite que já existe.
export default function DetalheTransportadoraModal({
  transportadoraId,
  embarcadorId,
  onClose,
  onChanged,
  onRemoverDaCarteira,
  acessoPortal,
  conviteHabilitado = true,
  onReenviar,
  onCancelar,
}) {
  const [abaAtiva, setAbaAtiva] = useState("visao-geral");
  const [detalhe, setDetalhe] = useState(null);
  const [capacidades, setCapacidades] = useState(null);
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState("");

  const carregar = useCallback(async () => {
    if (!transportadoraId) return;
    setLoading(true);
    setErro("");
    try {
      const [detalheRes, capacidadesRes] = await Promise.all([
        api.get(`/transportadoras/${transportadoraId}/detalhe`, { params: embarcadorId ? { embarcadorId } : {} }),
        getCapacidadesTransportadora(transportadoraId, embarcadorId),
      ]);
      setDetalhe(detalheRes.data);
      setCapacidades(capacidadesRes);
    } catch (e) {
      const mensagem = e && e.response && e.response.data && e.response.data.error;
      setErro(mensagem || "Falha ao carregar a transportadora.");
    } finally {
      setLoading(false);
    }
  }, [transportadoraId, embarcadorId]);

  useEffect(() => {
    setAbaAtiva("visao-geral");
    setDetalhe(null);
    setCapacidades(null);
    carregar();
  }, [carregar]);

  const capacidadeList = (capacidades && capacidades.capacidades) || [];
  const acesso = acessoPortal ? conviteEstado(acessoPortal, { conviteHabilitado }) : null;
  const conviteAtual = acesso && acesso.convite;

  return (
    <Modal isOpen={!!transportadoraId} toggle={onClose} modalClassName="modal-align-top" fade={false} size="xl">
      <div style={headStyle}>
        <div style={{ minWidth: 0, display: "flex", alignItems: "flex-start", gap: "0.6rem" }}>
          {detalhe && <TransportadoraMonogram nome={detalhe.fantasia || detalhe.razaoSocial} size={36} logoUrl={detalhe.logoUrl} />}
          <div style={{ minWidth: 0 }}>
            <div style={drawerEyebrowStyle}>
              Transportadora{capacidades ? ` · ${capacidades.fonte}` : ""}
            </div>
            <div style={drawerTitleStyle}>{detalhe ? detalhe.fantasia || detalhe.razaoSocial : "Carregando..."}</div>
            {detalhe && <div style={drawerSubtitleStyle}>{formatDoc(detalhe.cnpjCpf)}</div>}
          </div>
        </div>
        <button type="button" onClick={onClose} style={drawerCloseButtonStyle} aria-label="Fechar detalhe">
          ×
        </button>
      </div>

      <div style={tabsStyle}>
        <Nav style={{ margin: 0 }}>
          {TABS.map((tab) => (
            <NavItem key={tab}>
              <NavLink style={navLinkStyle(abaAtiva === tab)} onClick={() => setAbaAtiva(tab)}>
                {TAB_LABELS[tab]}
              </NavLink>
            </NavItem>
          ))}
        </Nav>
      </div>

      <ModalBody style={bodyStyle}>
        {loading ? (
          <div style={{ textAlign: "center", padding: "2rem" }}>
            <Spinner size="sm" color="info" />
          </div>
        ) : erro ? (
          <div style={drawerEmptyStyle}>{erro}</div>
        ) : (
          <TabContent activeTab={abaAtiva}>
            <TabPane tabId="visao-geral">
              {detalhe && (
                <div>
                  <div style={drawerFieldRowStyle}>
                    <span style={drawerFieldLabelStyle}>Razão Social</span>
                    <span style={drawerFieldValueStyle}>{detalhe.razaoSocial}</span>
                  </div>
                  <div style={drawerFieldRowStyle}>
                    <span style={drawerFieldLabelStyle}>Status</span>
                    <span style={drawerFieldValueStyle}>{detalhe.status ? "Ativo" : "Inativo"}</span>
                  </div>
                  <div style={drawerFieldRowStyle}>
                    <span style={drawerFieldLabelStyle}>Fonte</span>
                    <span style={drawerFieldValueStyle}>{(capacidades && capacidades.fonte) || "Sem conector"}</span>
                  </div>

                  <div style={{ marginTop: "0.9rem" }}>
                    <div style={{ ...drawerFieldLabelStyle, marginBottom: "0.5rem", fontSize: "0.72rem", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                      Capacidades
                    </div>
                    {capacidadeList.length ? (
                      <div style={{ display: "flex", flexDirection: "column", gap: "0.45rem" }}>
                        {capacidadeList.map((c) => (
                          <CapacidadeBadge key={c.codigo} {...c} />
                        ))}
                      </div>
                    ) : (
                      <div style={drawerEmptyStyle}>Nenhuma capacidade de conector ativa para esta transportadora ainda.</div>
                    )}
                  </div>

                  {acesso && (
                    <div style={{ marginTop: "1.4rem", paddingTop: "1rem", borderTop: `1px solid ${AUTOPILOT_COLORS.borderFaint}` }}>
                      <div style={{ ...drawerFieldLabelStyle, marginBottom: "0.5rem", fontSize: "0.72rem", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                        Acesso ao portal
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", flexWrap: "wrap" }}>
                        <span className={`onb-badge ${acesso.badge}`}>{acesso.rotulo}</span>
                        {conviteAtual && conviteAtual.email && (
                          <span style={{ ...drawerFieldValueStyle, fontSize: "0.8rem" }}>{conviteAtual.email}</span>
                        )}
                      </div>
                      {acesso.tipo === "falha" && (
                        <div style={{ ...drawerEmptyStyle, marginTop: "0.4rem" }}>O e-mail do convite não saiu. Reenvie ou confira o endereço.</div>
                      )}
                      {acesso.tipo === "curadoria" && (
                        <div style={{ ...drawerEmptyStyle, marginTop: "0.4rem" }}>A matriz desta transportadora ainda não é a 0001: o convite sai quando a Everlog ajustar o cadastro.</div>
                      )}
                      <div style={{ display: "flex", gap: "0.5rem", marginTop: "0.6rem", flexWrap: "wrap" }}>
                        {acesso.podeReenviar && onReenviar && (
                          <Button size="sm" color="info" outline onClick={() => onReenviar(conviteAtual)}>
                            Reenviar convite
                          </Button>
                        )}
                        {acesso.podeCancelar && onCancelar && (
                          <Button size="sm" color="secondary" outline onClick={() => onCancelar(conviteAtual)}>
                            Cancelar convite
                          </Button>
                        )}
                      </div>
                    </div>
                  )}

                  {onRemoverDaCarteira && (
                    <div style={{ marginTop: "1.4rem", paddingTop: "1rem", borderTop: `1px solid ${AUTOPILOT_COLORS.borderFaint}` }}>
                      <Button size="sm" color="danger" outline onClick={onRemoverDaCarteira}>
                        Remover da carteira
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </TabPane>

            <TabPane tabId="configuracao">
              {abaAtiva === "configuracao" && (
                <IntegracaoTab
                  transportadoraId={transportadoraId}
                  embarcadorId={embarcadorId}
                  transportadora={detalhe ? { nome: detalhe.fantasia || detalhe.razaoSocial, cnpj: formatDoc(detalhe.cnpjCpf) } : null}
                  onChanged={() => {
                    carregar();
                    if (onChanged) onChanged();
                  }}
                />
              )}
            </TabPane>

            <TabPane tabId="tabela-frete">
              {abaAtiva === "tabela-frete" && (
                <FreteTabelaResumoTab transportadoraId={transportadoraId} embarcadorId={embarcadorId} />
              )}
            </TabPane>

            <TabPane tabId="sftp">
              {abaAtiva === "sftp" && <SftpTab sftp={capacidades && capacidades.sftp} />}
            </TabPane>
          </TabContent>
        )}
      </ModalBody>
    </Modal>
  );
}
